import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AnalyticsActions,
  AnalyticsSelectors,
  AuthSelectors,
} from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

declare global {
  interface Window {
    powerbi: any;
  }
}

@Component({
  selector: 'app-power-bi-embed',
  templateUrl: './power-bi-embed.component.html',
  styleUrls: ['./power-bi-embed.component.scss'],
})
export class PowerBiEmbedComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('embedContainer', { static: true }) embedContainer!: ElementRef;

  models = window['powerbi-client'].models;
  loading: boolean = false;
  embedToken: string = null;
  embedUrl: string = null;
  reportId: string = null;
  userId: string = null;
  reportName: string = '';
  filter = {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: {
      table: 'Portal_Users',
      column: 'Portal_User_ID',
    },
    operator: 'In',
    values: [null],
    filterType: this.models.FilterType.BasicFilter,
  };

  subscriptions: Subscription = new Subscription();

  constructor(
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    public router: Router
  ) {}

  ngOnInit(): void {
    // Listen for route parameter changes
    this.activatedRoute.params.subscribe(({ reportId }) => {
      this.loading = true;
      this.reportId = reportId;
      this.embedToken = null;
      this.embedUrl = null;
      this.reportName = null;

      this.getPowerBiReportDetails();
    });

    // Subscribe to user ID and fetch details
    this.subscriptions.add(
      this.store
        .select(AuthSelectors.getUserIds)
        .pipe(filter(userIdCheck => !!userIdCheck))
        .subscribe(userIds => {
          this.userId = userIds.id;
          this.filter.values = [userIds.id];
          this.getPowerBiReportDetails();
        })
    );
  }

  ngAfterViewInit(): void {
    this.subscribeToPowerBiReportDetails();
  }

  private getPowerBiReportDetails(): void {
    this.subscriptions.add(
      this.store
        .select(AnalyticsSelectors.getReportList)
        .subscribe(reportsList => {
          if (reportsList.find(rl => rl.id === this.reportId)) {
            if (this.userId) {
              this.store.dispatch(
                AnalyticsActions.getPowerBiReportDetails({
                  requestBody: {
                    reportId: this.reportId,
                    userId: this.userId,
                    userRole: 'Portal User',
                  },
                })
              );
            }
          } else {
            this.router.navigate(['/analytics/reports']);
          }
        })
    );
  }

  private subscribeToPowerBiReportDetails(): void {
    this.subscriptions.add(
      this.store
        .select(AnalyticsSelectors.getPowerBiReportDetails)
        .pipe(filter(powerBiReportDetails => !!powerBiReportDetails))
        .subscribe(powerBiReportDetails => {
          if (powerBiReportDetails.embedToken) {
            this.embedToken = powerBiReportDetails.embedToken;
            this.embedUrl = powerBiReportDetails.embedUrl;
            this.reportId = powerBiReportDetails.reportId;
            this.reportName = powerBiReportDetails.reportName;

            // Embed the report each time the details update
            this.embedPowerBIReport();
          }
        })
    );
  }

  private embedPowerBIReport() {
    // Cleanup any previous report instance
    this.cleanupReport();

    const config = {
      type: 'report',
      tokenType: this.models.TokenType.Embed,
      accessToken: this.embedToken,
      embedUrl: this.embedUrl,
      id: this.reportId,
      permissions: this.models.Permissions.All,
      settings: {
        panes: {
          filters: { visible: false },
          pageNavigation: { visible: false },
        },
        bars: { statusBar: { visible: true } },
      },
      viewMode: this.models.ViewMode.View,
    };

    const embedContainer = this.embedContainer.nativeElement;
    const report = window.powerbi.embed(embedContainer, config);

    report.on('loaded', () => this.applyFilter(report));
  }

  private async applyFilter(report) {
    try {
      const pages = await report.getPages();
      const activePage = pages.find(page => page.isActive);

      if (activePage) {
        await report.updateFilters(this.models.FiltersOperations.Replace, [
          this.filter,
        ]);
      }

      this.loading = false;
    } catch (error) {
      console.error('Error applying filter:', error);
    }
  }

  private cleanupReport(): void {
    const embedContainer = this.embedContainer.nativeElement;

    window.powerbi.reset(embedContainer);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.cleanupReport();
  }
}
